/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import type { OarTaskService } from '../../services/oar-task-service';
import type { OarStatementContentModel } from '../../models/oar-statement-content-model';
import type { StatementComparisonOptions } from '../../services/oar-task-service';
import type { GenerateComparisonPageCommand } from '../claim-analysis-command';

/**
 * 生成对照页内容命令参数
 */
export interface GenerateComparisonPageCommandParams {
    taskId: string;
    options: StatementComparisonOptions;
}

/**
 * 生成对照页内容命令实现
 *
 * 执行流程：
 * 1. 调用 OarTaskService.generateStatementComparisonContent 生成对照页
 * 2. 根据字段类型更新到 OarStatementContentModel 对应字段
 * 3. 返回对照页内容
 */
export class GenerateComparisonPageCommandImpl implements GenerateComparisonPageCommand {
    private _service: OarTaskService;
    private _model: OarStatementContentModel;
    private _previousContent?: string;
    private _previousGeneratedAt?: number;
    private _previousWordCount?: number;
    private _fieldType: 'claim' | 'description';
    private _params: GenerateComparisonPageCommandParams;

    constructor(
        service: OarTaskService,
        model: OarStatementContentModel,
        params: GenerateComparisonPageCommandParams,
    ) {
        this._service = service;
        this._model = model;
        this._params = params;
        this._fieldType = params.options.field;
    }

    /**
     * 检查命令是否可以执行
     *
     * @returns 是否可以执行
     */
    canExecute(): boolean {
        return !!(
            this._params.taskId &&
            this._params.options?.statementDocument &&
            ['claim', 'description'].includes(this._params.options.field)
        );
    }

    /**
     * 执行生成对照页内容命令
     *
     * @returns 对照页内容字符串
     * @throws 当参数无效或生成失败时抛出错误
     */
    async execute(): Promise<string> {
        if (!this.canExecute()) {
            throw new Error(
                'Invalid command parameters: taskId, statementDocument, patentText, and valid field are required',
            );
        }

        try {
            // 1. 保存当前数据用于撤销
            const currentData = this._model.modelData;
            if (this._fieldType === 'claim') {
                this._previousContent = currentData.claimComparisonContent;
                this._previousGeneratedAt = currentData.claimComparisonGeneratedAt;
                this._previousWordCount = currentData.claimComparisonWordCount;
            } else {
                this._previousContent = currentData.descriptionComparisonContent;
                this._previousGeneratedAt = currentData.descriptionComparisonGeneratedAt;
                this._previousWordCount = currentData.descriptionComparisonWordCount;
            }

            // 2. 调用服务生成对照页内容
            const content = await this._service.generateStatementComparisonContent(
                this._params.taskId,
                this._params.options,
            );

            // 3. 更新 Model
            const now = Date.now();
            if (this._fieldType === 'claim') {
                this._model.update(state => ({
                    ...state,
                    taskId: this._params.taskId,
                    claimComparisonContent: content,
                    claimComparisonGeneratedAt: now,
                    claimComparisonWordCount: content.length,
                }));
            } else {
                this._model.update(state => ({
                    ...state,
                    taskId: this._params.taskId,
                    descriptionComparisonContent: content,
                    descriptionComparisonGeneratedAt: now,
                    descriptionComparisonWordCount: content.length,
                }));
            }

            return content;
        } catch (error) {
            // 如果执行失败，恢复之前的状态
            if (this._previousContent !== undefined) {
                if (this._fieldType === 'claim') {
                    this._model.update(state => ({
                        ...state,
                        claimComparisonContent: this._previousContent,
                        claimComparisonGeneratedAt: this._previousGeneratedAt,
                        claimComparisonWordCount: this._previousWordCount,
                    }));
                } else {
                    this._model.update(state => ({
                        ...state,
                        descriptionComparisonContent: this._previousContent,
                        descriptionComparisonGeneratedAt: this._previousGeneratedAt,
                        descriptionComparisonWordCount: this._previousWordCount,
                    }));
                }
            }
            throw error;
        }
    }

    /**
     * 撤销命令，恢复到执行前的状态
     */
    async undo(): Promise<void> {
        if (this._previousContent !== undefined) {
            if (this._fieldType === 'claim') {
                this._model.update(state => ({
                    ...state,
                    claimComparisonContent: this._previousContent,
                    claimComparisonGeneratedAt: this._previousGeneratedAt,
                    claimComparisonWordCount: this._previousWordCount,
                }));
            } else {
                this._model.update(state => ({
                    ...state,
                    descriptionComparisonContent: this._previousContent,
                    descriptionComparisonGeneratedAt: this._previousGeneratedAt,
                    descriptionComparisonWordCount: this._previousWordCount,
                }));
            }

            // 清理保存的数据
            this._previousContent = undefined;
            this._previousGeneratedAt = undefined;
            this._previousWordCount = undefined;
        }
    }
}
